<template>
  <Modal
    v-model="visible"
    :title="langMap.viewTopology[lang]"
    width="1200"
    class-name="topology-modal-top"
    @on-cancel="handleCancel"
  >
    <div class="topology-container">
      <canvas
        ref="canvas"
        :width="canvasWidth"
        :height="canvasHeight"
        @mousedown="handleCanvasMouseDown"
        @mousemove="handleCanvasMouseMove"
        @mouseup="handleCanvasMouseUp"
        @mouseleave="handleCanvasMouseUp"
        @wheel="handleCanvasWheel"
      ></canvas>

      <div class="statistics">
        <p><strong>{{ langMap.nodeCount[lang] }}:</strong> {{ nodeCount }}</p>
        <p><strong>{{ langMap.edgeCount[lang] }}:</strong> {{ edgeCount }}</p>
        <p><strong>{{ langMap.bidirectionalEdges[lang] }}:</strong> {{ bidirectionalCount }}</p>
        <p><strong>{{ langMap.unidirectionalEdges[lang] }}:</strong> {{ unidirectionalCount }}</p>
      </div>
    </div>

    <div slot="footer">
      <Button @click="handleCancel">{{ langMap.close[lang] }}</Button>
    </div>
  </Modal>
</template>

<script>
import langMap from '@/utils/language';

export default {
  name: 'TopologyViewDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    graphData: {
      type: Object,
      default: () => ({ nodes: [], edges: [] })
    }
  },
  data() {
    return {
      langMap,
      canvasWidth: 1150,
      canvasHeight: 700,
      nodePositions: {},
      draggingNode: null,
      dragOffset: { x: 0, y: 0 },
      // 画布平移和缩放
      isDraggingCanvas: false,
      dragStartPos: { x: 0, y: 0 },
      panOffset: { x: 0, y: 0 },
      scale: 1,
      minScale: 0.1,
      maxScale: 5
    };
  },
  computed: {
    lang() {
      return this.$store.state.app.language;
    },
    visible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    nodeCount() {
      return this.graphData.nodes ? this.graphData.nodes.length : 0;
    },
    edgeCount() {
      return this.graphData.edges ? this.graphData.edges.length : 0;
    },
    bidirectionalCount() {
      if (!this.graphData.edges) return 0;
      return this.graphData.edges.filter(e => e.bidirectional !== false).length;
    },
    unidirectionalCount() {
      if (!this.graphData.edges) return 0;
      return this.graphData.edges.filter(e => e.bidirectional === false).length;
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.adjustCanvasSize();
        this.$nextTick(() => {
          this.calculateNodePositions();
          this.$nextTick(() => {
            this.renderTopology();
          });
        });
      }
    }
  },
  methods: {
    adjustCanvasSize() {
      // 计算可用的视口高度
      const viewportHeight = window.innerHeight;
      // 弹窗顶部距离 + 标题栏 + 统计信息 + 底部按钮 + 边距
      const reservedHeight = 20 + 60 + 100 + 70 + 40;
      const maxCanvasHeight = viewportHeight - reservedHeight;
      
      // 设置画布高度，最小500，最大为计算出的可用高度
      this.canvasHeight = Math.max(500, Math.min(700, maxCanvasHeight));
    },

    calculateNodePositions() {
      const nodes = this.graphData.nodes || [];
      
      if (nodes.length === 0) {
        return;
      }

      // 使用实际的经纬度坐标
      // 找出坐标范围
      let minLat = Infinity, maxLat = -Infinity;
      let minLng = Infinity, maxLng = -Infinity;

      nodes.forEach(node => {
        const lat = node.latitude;
        const lng = node.longitude;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
      });

      // 计算坐标范围
      const latRange = maxLat - minLat || 0.001;
      const lngRange = maxLng - minLng || 0.001;

      // 留出边距
      const padding = 50;
      const availableWidth = this.canvasWidth - 2 * padding;
      const availableHeight = this.canvasHeight - 2 * padding;

      // 计算缩放比例（保持纵横比）
      const scaleX = availableWidth / lngRange;
      const scaleY = availableHeight / latRange;
      const scale = Math.min(scaleX, scaleY);

      // 计算偏移量以居中显示
      const scaledWidth = lngRange * scale;
      const scaledHeight = latRange * scale;
      const offsetX = padding + (availableWidth - scaledWidth) / 2;
      const offsetY = padding + (availableHeight - scaledHeight) / 2;

      // 转换每个节点的坐标
      nodes.forEach(node => {
        // 经度映射到X轴，纬度映射到Y轴（注意Y轴需要翻转）
        const x = offsetX + (node.longitude - minLng) * scale;
        const y = offsetY + (maxLat - node.latitude) * scale; // Y轴翻转

        this.nodePositions[node.id] = { x, y };
      });
    },

    renderTopology() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 保存当前状态
      ctx.save();

      // 应用平移和缩放变换
      ctx.translate(this.panOffset.x, this.panOffset.y);
      ctx.scale(this.scale, this.scale);

      // 绘制边
      this.drawEdges(ctx);

      // 绘制节点
      this.drawNodes(ctx);

      // 恢复状态
      ctx.restore();
    },

    drawEdges(ctx) {
      const edges = this.graphData.edges || [];

      edges.forEach(edge => {
        const fromNodeId = edge.fromNodeId || edge.from;
        const toNodeId = edge.toNodeId || edge.to;
        const fromPos = this.nodePositions[fromNodeId];
        const toPos = this.nodePositions[toNodeId];

        if (!fromPos || !toPos) return;

        // bidirectional 为 true 表示双向，false 表示单向
        const color = edge.bidirectional === false ? '#ff7800' : '#3388ff';
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;

        ctx.beginPath();
        ctx.moveTo(fromPos.x, fromPos.y);
        ctx.lineTo(toPos.x, toPos.y);
        ctx.stroke();

        // 绘制箭头
        this.drawArrow(ctx, fromPos, toPos, color, edge.bidirectional !== false);
      });
    },

    drawArrow(ctx, from, to, color, isBidirectional) {
      const headlen = 10;
      const angle = Math.atan2(to.y - from.y, to.x - from.x);

      // 计算箭头位置（在线的中点或终点）
      const arrowX = isBidirectional ? (from.x + to.x) / 2 : to.x;
      const arrowY = isBidirectional ? (from.y + to.y) / 2 : to.y;

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(arrowX, arrowY);
      ctx.lineTo(
        arrowX - headlen * Math.cos(angle - Math.PI / 6),
        arrowY - headlen * Math.sin(angle - Math.PI / 6)
      );
      ctx.lineTo(
        arrowX - headlen * Math.cos(angle + Math.PI / 6),
        arrowY - headlen * Math.sin(angle + Math.PI / 6)
      );
      ctx.closePath();
      ctx.fill();

      // 双向边绘制反向箭头
      if (isBidirectional) {
        ctx.beginPath();
        ctx.moveTo(arrowX, arrowY);
        ctx.lineTo(
          arrowX + headlen * Math.cos(angle - Math.PI / 6),
          arrowY + headlen * Math.sin(angle - Math.PI / 6)
        );
        ctx.lineTo(
          arrowX + headlen * Math.cos(angle + Math.PI / 6),
          arrowY + headlen * Math.sin(angle + Math.PI / 6)
        );
        ctx.closePath();
        ctx.fill();
      }
    },

    drawNodes(ctx) {
      const nodes = this.graphData.nodes || [];

      nodes.forEach(node => {
        const pos = this.nodePositions[node.id];
        if (!pos) return;

        // 绘制节点圆圈
        ctx.fillStyle = '#3388ff';
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;

        ctx.beginPath();
        ctx.arc(pos.x, pos.y, 15, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();

        // 绘制节点ID
        ctx.fillStyle = '#fff';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(node.id.toString(), pos.x, pos.y);
      });
    },

    handleCanvasMouseDown(e) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // 转换为画布坐标（考虑平移和缩放）
      const canvasX = (x - this.panOffset.x) / this.scale;
      const canvasY = (y - this.panOffset.y) / this.scale;

      // 检查是否点击了节点
      let nodeClicked = false;
      for (const [nodeId, pos] of Object.entries(this.nodePositions)) {
        const distance = Math.sqrt((canvasX - pos.x) ** 2 + (canvasY - pos.y) ** 2);
        if (distance <= 15) {
          this.draggingNode = nodeId;
          this.dragOffset = { x: canvasX - pos.x, y: canvasY - pos.y };
          nodeClicked = true;
          break;
        }
      }

      // 如果没有点击节点，则拖动画布
      if (!nodeClicked) {
        this.isDraggingCanvas = true;
        this.dragStartPos = { x, y };
      }
    },

    handleCanvasMouseMove(e) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      if (this.draggingNode) {
        // 拖动节点
        const canvasX = (x - this.panOffset.x) / this.scale;
        const canvasY = (y - this.panOffset.y) / this.scale;

        this.nodePositions[this.draggingNode] = {
          x: canvasX - this.dragOffset.x,
          y: canvasY - this.dragOffset.y
        };

        this.renderTopology();
      } else if (this.isDraggingCanvas) {
        // 拖动画布
        const dx = x - this.dragStartPos.x;
        const dy = y - this.dragStartPos.y;

        this.panOffset.x += dx;
        this.panOffset.y += dy;

        this.dragStartPos = { x, y };
        this.renderTopology();
      }
    },

    handleCanvasMouseUp() {
      this.draggingNode = null;
      this.isDraggingCanvas = false;
    },

    handleCanvasWheel(e) {
      e.preventDefault();

      const rect = this.$refs.canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // 计算缩放前鼠标在画布上的位置
      const canvasX = (mouseX - this.panOffset.x) / this.scale;
      const canvasY = (mouseY - this.panOffset.y) / this.scale;

      // 计算新的缩放比例
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale * delta));

      // 计算新的偏移量，使鼠标位置保持不变
      this.panOffset.x = mouseX - canvasX * newScale;
      this.panOffset.y = mouseY - canvasY * newScale;

      this.scale = newScale;
      this.renderTopology();
    },

    handleCancel() {
      this.visible = false;
    }
  }
};
</script>

<style scoped>
.topology-container {
  position: relative;
  height: 100%;
  overflow: auto;
}

canvas {
  border: 1px solid #dcdee2;
  border-radius: 4px;
  cursor: grab;
  display: block;
}

canvas:active {
  cursor: grabbing;
}

.statistics {
  margin-top: 15px;
  padding: 10px;
  background: #f8f8f9;
  border-radius: 4px;
}

.statistics p {
  margin: 5px 0;
}
</style>

<style>
/* 全局样式，让弹窗显示在顶部 */
.topology-modal-top .ivu-modal {
  top: 3px !important;
}

.topology-modal-top .ivu-modal-body {
  max-height: calc(100vh - 115px);
  overflow-y: auto;
}
</style>
